import { View } from '@play-co/timestep-core/ui';
import { captureGenericError } from 'src/lib/sentry';
import LangBitmapFontTextView from '../components/LangBitmapFontTextView';

export type HyperLinkDefinition = {
  text: LangBitmapFontTextView;
  regex: RegExp;
  onClick: () => void;
  color?: string;
};

/**
 * Add hyperlinks to a text.
 * Note: Line breaks in between the link text are not supported.
 */
export function createHyperlink(definition: HyperLinkDefinition) {
  definition.text.font?.doOnLoad(() => {
    try {
      createHyperlikInternal(definition);
    } catch (e) {
      captureGenericError('Cannot create hyperlink.', e);
    }
  });
}

function createHyperlikInternal({
  text,
  regex,
  onClick,
  color = '#479BBF',
}: HyperLinkDefinition) {
  getRegexRects(text, regex).forEach(
    ({ x, y, width, height, startIndex, lastIndex }) => {
      const originalText = text.localeText?.() ?? '';
      text.localeText = () =>
        `${originalText.substring(
          0,
          startIndex,
        )}[color=${color}]${originalText.substring(
          startIndex,
          lastIndex + 1,
        )}[/color]${originalText.substring(
          lastIndex + 1,
          originalText.length,
        )}`;

      const clickable = new View({
        superview: text,
        x,
        y,
        canHandleEvents: true,
        width,
        height,
      });

      new View({
        superview: text,
        x: x,
        y: y + height,
        backgroundColor: color,
        canHandleEvents: true,
        width: width,
        height: 2,
      });

      clickable.onInputSelect = onClick;
    },
  );
}

export function getRegexRects(text: LangBitmapFontTextView, regex: RegExp) {
  const rects: {
    x: number;
    y: number;
    width: number;
    height: number;
    startIndex: number;
    lastIndex: number;
  }[] = [];

  let match;
  const string = text.localeText?.() ?? '';
  while ((match = regex.exec(string))) {
    const stringStartIndex = regex.lastIndex - match[0].length;
    let stringLastIndex = regex.lastIndex - 1;

    const ignored = countIgnoredChars(string.substring(0, stringStartIndex));
    const ignoredBetween = countIgnoredChars(
      string.substring(stringStartIndex, stringLastIndex + 1),
    );

    const startIndex = stringStartIndex - ignored;
    const lastIndex = stringLastIndex - (ignored + ignoredBetween);

    if (!text.characters) {
      continue;
    }

    const firstChar = text.characters[startIndex];
    const lastChar = text.characters[lastIndex];

    rects.push({
      x: firstChar.x,
      y: firstChar.y,
      width: lastChar.x - firstChar.x + lastChar.width,
      height: Math.max(firstChar.height, lastChar.height),
      startIndex: stringStartIndex,
      lastIndex: stringLastIndex,
    });
  }

  return rects;
}

function countIgnoredChars(text: string) {
  const ignore = /( |\n|\[color=#[A-Fa-f0-9]{6}\]|\[\/color\])/g;
  return (
    text.match(ignore)?.reduce((acc, curr) => (acc += curr.length), 0) ?? 0
  );
}
